import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageProvider";

export default function ContactForm() {
  const { t } = useT();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error(t("contact.toast_required"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.toast_success"));
      setForm({ first_name: "", last_name: "", email: "", message: "" });
    } catch (err) {
      toast.error(t("contact.toast_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative w-full grain py-28 md:py-40"
      style={{ background: "#211512" }}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7 }}
            className="label-tracked"
            style={{ color: "#C5A059" }}
            data-testid="contact-eyebrow"
          >
            {t("contact.eyebrow")}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="contact-headline"
            className="font-serif font-light text-[#FFF8EE] mt-6 leading-[1]"
            style={{ fontSize: "clamp(2.4rem, 5.6vw, 5rem)" }}
          >
            {t("contact.headline_1")}
            <br />
            <span className="italic" style={{ color: "#C5A059" }}>
              {t("contact.headline_2")}
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-8 text-[#FFF8EE]/70 max-w-md leading-relaxed"
            data-testid="contact-description"
          >
            {t("contact.description")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 border-t border-[#FFF8EE]/15 pt-8 space-y-3"
          >
            <p className="label-tracked text-[#C5A059]">{t("contact.direct")}</p>
            <p className="font-serif text-2xl text-[#FFF8EE]">
              tirami.info@gmail.com
            </p>
            <p className="text-[#FFF8EE]/60 text-sm">{t("contact.hours")}</p>
          </motion.div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          onSubmit={submit}
          className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8"
          data-testid="contact-form"
        >
          <Field
            label={t("contact.first_name")}
            id="first_name"
            value={form.first_name}
            onChange={update("first_name")}
            placeholder={t("contact.placeholder_first")}
          />
          <Field
            label={t("contact.last_name")}
            id="last_name"
            value={form.last_name}
            onChange={update("last_name")}
            placeholder={t("contact.placeholder_last")}
          />
          <Field
            label={t("contact.email")}
            id="email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder={t("contact.placeholder_email")}
            className="sm:col-span-2"
          />

          <div className="sm:col-span-2">
            <label
              htmlFor="message"
              className="label-tracked text-[#C5A059] block mb-3"
            >
              {t("contact.message")}
            </label>
            <textarea
              id="message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={update("message")}
              placeholder={t("contact.placeholder_message")}
              rows={5}
              className="w-full bg-transparent border-b border-[#FFF8EE]/25 pb-3 text-[#FFF8EE] placeholder-[#FFF8EE]/35 outline-none focus:border-[#C5A059] transition-colors resize-none"
            />
          </div>

          <div className="sm:col-span-2 pt-4 flex items-center justify-between gap-6 flex-wrap">
            <p className="text-[#FFF8EE]/45 text-xs max-w-xs">
              {t("contact.disclaimer")}
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-3 btn-gold px-10 py-4 label-tracked disabled:opacity-50"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
              {loading ? t("contact.sending") : t("contact.send")}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, id, type = "text", value, onChange, placeholder, className = "" }) {
  return (
    <div className={className}>
      <label htmlFor={id} className="label-tracked text-[#C5A059] block mb-3">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`contact-input-${id}`}
        className="w-full bg-transparent border-b border-[#FFF8EE]/25 pb-3 text-[#FFF8EE] placeholder-[#FFF8EE]/35 outline-none focus:border-[#C5A059] transition-colors"
      />
    </div>
  );
}
